import { createCoreLaser, getCoreLaserDuration } from './coreLaser';
import * as THREE from 'three';
import { createCoreModel } from './coreModel';
import { createCoreBoardSpellScene } from './coreBoardSpellScene';
import { getCoreAppearance } from './coreAppearance';
import { BOSS_MAX_HP, CORE_PHASE_RANK, getCoreVisualState } from './corePhases';
import {
  CORE_BOARD_POSITION,
  CORE_BOARD_FACING_TARGET,
  CORE_BOARD_SCALE,
  CORE_BOARD_STRIKE_TARGET,
} from './coreBoardLayout';

// Lives in the board renderer; combat timing remains owned by BossBattle.
export const createCoreBoardScene = (scene) => {
  const spellScene = createCoreBoardSpellScene(scene);
  let laser = null;
  let attackStage = null;
  let stageStartedAt = 0;
  let model = null;
  let phaseLevel = null;
  const highlight = new THREE.Color(0xffffff);
  const update = ({ boss, elapsed, delta, camera, reduceMotion, target }) => {
    spellScene.update(boss?.spell, reduceMotion);
    if (attackStage !== boss?.stage) {
      attackStage = boss?.stage;
      stageStartedAt = elapsed;
    }
    if (boss && !laser) laser = createCoreLaser(scene);
    laser?.update({
      stage: boss?.stage,
      progress:
        ((elapsed - stageStartedAt) * 1000) /
        (getCoreLaserDuration(boss?.stage, reduceMotion, boss?.attackType) ||
          1),
      origin: CORE_BOARD_STRIKE_TARGET,
      target,
      camera,
      outcome: boss?.outcome || 'miss',
      corePhase: boss?.phase,
      attackType: boss?.attackType,
      reduceMotion,
    });
    if (!boss) {
      if (model) model.core.visible = false;
      return;
    }
    if (!model) {
      if (boss.spell && !reduceMotion)
        phaseLevel = CORE_PHASE_RANK[boss.spell.beforePhase] || 0;
      model = createCoreModel(scene, {
        hp: boss.hp,
        position: CORE_BOARD_POSITION,
      });
      model.core.name = 'board-glitch-core';
      model.core.scale.setScalar(CORE_BOARD_SCALE);
    }
    const {
      core,
      coreMaterial,
      fracture,
      heart,
      rings,
      shards,
      shardMaterial,
    } = model;
    core.visible = boss.hp > 0;
    if (!core.visible) return;
    const targetLevel = CORE_PHASE_RANK[boss.phase] || 0;
    phaseLevel =
      phaseLevel === null || reduceMotion
        ? targetLevel
        : THREE.MathUtils.damp(phaseLevel, targetLevel, 4, delta);
    const visual = getCoreVisualState(phaseLevel);
    model.coreMesh.visible = visual.heart < 0.25;
    const appearance = getCoreAppearance(boss.phase);
    const heat = visual.meltdown || 1 - boss.hp / BOSS_MAX_HP;
    fracture.syncHealth(boss.hp);
    fracture.setOpenness(visual.shellOpenness);
    coreMaterial.color.setHex(appearance.color);
    coreMaterial.emissive.setHex(appearance.emissive);
    const hit = boss.combat
      ? Math.max(
          0,
          1 - (Date.now() - boss.combat.startTime - boss.combat.hitAt) / 650
        )
      : 0;
    const impacted =
      boss.combat && Date.now() - boss.combat.startTime >= boss.combat.hitAt;
    coreMaterial.emissiveIntensity =
      1.3 + heat * 2.8 + (impacted ? hit * 3 : 0) + (boss.spell ? 2 : 0);
    coreMaterial.opacity =
      0.95 -
      visual.fracture * 0.08 -
      visual.emergence * 0.12 -
      visual.evolution * 0.12 -
      visual.meltdown * 0.22;
    fracture.wire.material.color.copy(coreMaterial.color).lerp(highlight, 0.48);
    core.position.y =
      CORE_BOARD_POSITION.y +
      (reduceMotion ? 0 : Math.sin(elapsed * 1.6) * visual.evolution * 0.055);
    // Keep the shell and heart pitched toward the defending die. Small idle
    // movements preserve that facing instead of spinning its front away.
    core.lookAt(target || CORE_BOARD_FACING_TARGET);
    if (!reduceMotion) {
      core.rotateX(Math.sin(elapsed * 0.7) * 0.04);
      core.rotateY(Math.sin(elapsed * 0.6) * 0.08);
      core.rotateZ(Math.sin(elapsed * 1.3) * visual.evolution * 0.035);
    }
    rings.forEach((ring, index) => {
      if (!reduceMotion) {
        ring.rotation.z += delta * ring.userData.speed * (1 + heat * 0.9);
        ring.rotation.x +=
          delta *
          ((index % 2 ? -0.17 : 0.11) +
            visual.evolution * 0.18 +
            visual.meltdown * 0.25);
      }
      ring.material.color
        .copy(ring.userData.baseColor)
        .lerp(
          coreMaterial.color,
          visual.fracture * 0.3 + visual.heart * 0.35 + visual.meltdown * 0.35
        );
      ring.scale.setScalar(
        (1 + visual.fracture * 0.08 + visual.emergence * 0.06) * 0.5
      );
    });
    shardMaterial.color.copy(coreMaterial.color).lerp(highlight, 0.25);
    shards.forEach((shard, index) => {
      shard.position.lerpVectors(
        shard.userData.basePosition,
        shard.userData.fracturePosition,
        visual.fracture
      );
      shard.position.lerp(
        shard.userData.crownPosition,
        visual.emergence * 0.35 + visual.evolution * 0.65
      );
      if (visual.meltdown > 0)
        shard.position.lerpVectors(
          shard.userData.crownPosition,
          shard.userData.meltdownPosition,
          visual.meltdown
        );
      shard.position.multiplyScalar(0.5);
      if (!reduceMotion) {
        shard.position.y +=
          Math.sin(elapsed * 2 + index) *
          visual.fracture *
          (1 - visual.evolution) *
          0.12;
        shard.rotation.x += delta * (0.4 + index * 0.03);
        shard.rotation.y -=
          delta *
          (0.55 + heat * 0.4 + visual.evolution * 0.55 + visual.meltdown * 1.1);
      }
    });
    core.updateMatrixWorld(true);
    heart.update({
      elapsed,
      evolution: visual.heart,
      meltdown: visual.meltdown,
      stage: 'ready',
      camera,
      reduceMotion,
      faceCamera: false,
    });
  };
  return { update };
};
